import React from 'react';
import { motion } from 'framer-motion';
import { 
  Compass, 
  Map, 
  CreditCard, 
  CheckCircle2, 
  Calendar,
  Send,
  ArrowRight,
  Sparkles,
  Search,
  MessageCircle,
  ShieldCheck
} from 'lucide-react';
import ScrollToTop from '../components/Common/ScrollToTop';

const BookingGuide: React.FC = () => {
  const steps = [
    {
      id: '01',
      title: 'Global Inspiration',
      desc: 'Explore our curated collections of East African expeditions across the Serengeti, Masai Mara, and beyond.',
      icon: <Compass className="w-6 h-6" />,
      details: [
        'Browse by destination or wildlife interest',
        'Use our AI assistant for personalized picks',
        'Save your favorite safaris to your wishlist'
      ]
    },
    {
      id: '02',
      title: 'Consult & Customize',
      desc: 'Connect with our expert safari architects to refine your itinerary or build a custom private journey.',
      icon: <MessageCircle className="w-6 h-6" />,
      details: [
        'Real-time WhatsApp & Email support',
        'Bespoke accommodation adjustments',
        'Special interest routing (Photography, Conservation)'
      ]
    },
    {
      id: '03',
      title: 'Secure Reservation',
      desc: 'Confirm your adventure with a secure deposit through our global payment infrastructure.',
      icon: <CreditCard className="w-6 h-6" />,
      details: [
        'All major credit cards & regional transfers',
        'Flexible 30% deposit to lock in dates',
        'Automated booking confirmation & invoice'
      ]
    },
    {
      id: '04',
      title: 'Prepare for Trek',
      desc: 'Receive your comprehensive preparation kit, including health protocols and packing intelligence.',
      icon: <Send className="w-6 h-6" />,
      details: [
        'Customized digital itinerary access',
        'Visa & Vaccination guidance',
        'The ultimate safari packing checklist'
      ]
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { staggerChildren: 0.2 }
    }
  } as const;

  const itemVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: { 
      opacity: 1, 
      x: 0,
      transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
    }
  } as const;

  return (
    <div className="min-h-screen bg-[#F8FBFF] font-sans selection:bg-primary-100">
      {/* Hero Section */}
      <div className="relative pt-32 pb-24 px-6 overflow-hidden bg-white">
        <div className="absolute inset-0 opacity-[0.02] pointer-events-none">
          <svg width="100%" height="100%"><rect width="100%" height="100%" fill="url(#faq-grid)" /></svg>
        </div>
        
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-4xl mx-auto text-center"
        >
          <div className="inline-flex items-center px-4 py-2 rounded-full bg-primary-50 border border-primary-100 mb-6 italic-shadows">
            <Sparkles className="w-4 h-4 text-primary-600 mr-2" />
            <span className="text-[10px] font-bold text-primary-600 uppercase tracking-widest luxury-font">Journey Protocol</span>
          </div>
          <h1 className="text-4xl md:text-6xl font-extrabold text-primary-900 tracking-tight font-serif mb-6">
            How to <span className="text-primary-600">Book Your Safari</span>
          </h1>
          <p className="text-lg text-neutral-500 font-medium max-w-2xl mx-auto">
            A seamless guide to navigating your way from dream to the heart of the East African savannah.
          </p>
        </motion.div>
      </div>

      <div className="max-w-5xl mx-auto px-6 pb-32">
        {/* Timeline Steps */}
        <motion.div 
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="relative mt-12"
        >
          {/* Vertical Line */}
          <div className="absolute left-[31px] top-0 bottom-0 w-px bg-neutral-100 md:left-1/2 md:-ml-px" />

          <div className="space-y-24">
            {steps.map((step, idx) => (
              <motion.div 
                key={idx} 
                variants={itemVariants}
                className={`relative flex flex-col md:flex-row items-center ${idx % 2 !== 0 ? 'md:flex-row-reverse' : ''}`}
              >
                {/* Step Content */}
                <div className="flex-1 w-full md:w-1/2 pb-12 md:pb-0">
                  <div className={`group p-8 rounded-[40px] bg-white border border-blue-50 shadow-[0_20px_60px_-20px_rgba(42,126,206,0.1)] hover:shadow-[0_40px_100px_-30px_rgba(42,126,206,0.15)] transition-all duration-500 ${idx % 2 !== 0 ? 'md:mr-12' : 'md:ml-12'}`}>
                    <div className="flex items-center space-x-3 text-primary-600 mb-4">
                      {step.icon}
                      <span className="text-xs font-bold uppercase tracking-widest luxury-font">Phase {step.id}</span>
                    </div>
                    <h3 className="text-2xl font-extrabold text-primary-900 font-serif mb-4 group-hover:text-primary-600 transition-colors">
                      {step.title}
                    </h3>
                    <p className="text-neutral-400 font-medium mb-8 leading-relaxed">
                      {step.desc}
                    </p>
                    <ul className="space-y-4">
                      {step.details.map((detail, dIdx) => (
                        <li key={dIdx} className="flex items-center text-xs font-bold text-neutral-500">
                          <CheckCircle2 className="w-4 h-4 text-primary-500 mr-3 flex-shrink-0" />
                          {detail}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Marker */}
                <div className="absolute left-0 md:left-1/2 top-0 md:top-1/2 -ml-0 md:-ml-8 -mt-0 md:-mt-8 w-16 h-16 rounded-full bg-white border-2 border-primary-50 shadow-xl flex items-center justify-center z-10">
                  <span className="text-xl font-black text-primary-900 font-serif">{step.id}</span>
                </div>

                <div className="hidden md:block flex-1 w-1/2" />
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Global Assurance */}
        <motion.div 
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-40 p-12 rounded-[48px] bg-white border border-blue-50 shadow-[0_40px_80px_-20px_rgba(42,126,206,0.08)] flex flex-col md:flex-row items-center justify-between gap-12"
        >
          <div className="max-w-md">
            <h2 className="text-3xl font-extrabold text-primary-900 font-serif mb-4">Safari Specialist Support</h2>
            <p className="text-neutral-500 font-medium mb-8">Our architects are available around the clock to ensure your booking experience is as premium as the safari itself.</p>
            <div className="flex items-center space-x-6">
              <div className="flex items-center text-xs font-bold text-primary-900">
                <ShieldCheck className="w-4 h-4 text-primary-600 mr-2" />
                Secure Protocol
              </div>
              <div className="flex items-center text-xs font-bold text-primary-900">
                <Calendar className="w-4 h-4 text-primary-600 mr-2" />
                Flexible Rescheduling
              </div>
            </div>
          </div>
          
          <button className="px-10 py-5 bg-blue-500 text-white rounded-full font-bold uppercase tracking-widest text-xs hover:bg-blue-600 transition-all shadow-2xl shadow-blue-500/30 group flex items-center">
            View Sample Itinerary
            <ArrowRight className="w-4 h-4 ml-4 group-hover:translate-x-2 transition-transform" />
          </button>
        </motion.div>
      </div>

      <ScrollToTop />
    </div>
  );
};

export default BookingGuide;
